/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    cloneDeep as _cloneDeep,
    isEqual,
} from 'lodash';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import TestIcon from '@mui/icons-material/Speed';

import ConfirmationDialog from '../../../components/confirmationdlg';
import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth from '../../../data/auth-service';
import ZeekService from '../../../data/zeek-service';
import TIService from '../../../data/ti-service';
import SuricataService from '../../../data/suricata-service';
import SystemService from '../../../data/system-service';
import { teal } from '../../../data/colors';

const useStyles = makeStyles(theme => ({
    justifySpace: {
        justifyContent: 'space-between',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    ruleGroups: {
        maxHeight: '150px',
    },
    rulesCard: {
        height: '50%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    controlBar: {
        display: 'none',
    },
    secondary: {
        color: theme.palette.secondary.main,
    },
    next: {
        color: teal[300],
    },
}));

const TISettings = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [tiImports, setTIImports] = useState({
        frequency: 60,
        retries: 1,
        ti_urls: [],
        timeout: 120,
        urls: [],
    });
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const zeekForm = useValidate({});
    const [, setManagedStatus] = useState(false);
    const [tiNext, setTINext] = useState();
    const tiSettingsPerms = Auth.permissions.get('ThreatIntelIndicators');

    useEffect(() => {
        getURLConf();
        getNextTISync();
        getManagedStatus();
    }, []);

    useEffect(() => {
        setChanges(!isEqual(tiImports, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiImports]);

    const getManagedStatus = () => {
        SuricataService.isManaged().then(res => {
            setManagedStatus(res.managed);
        }, err => {
            console.error('Unable to get managed status ::: ', err);
        });
    };

    const getURLConf = () => {
        TIService.getConf().then((res) => {
            if (!res.ti_urls || !res.ti_urls.length) {
                res.ti_urls = [{ url: '' }];
            }
            setOriginal(_cloneDeep(res));
            setTIImports(res);
        });
    };

    const haveImports = useMemo(() => {
        return tiImports.ti_urls.filter(u => u.url.length > 0).length > 0;
    }, [tiImports]);

    const getNextTISync = () => {
        TIService.getNextTISync().then((res) => {
            res && setTINext(res);
        });
    };

    const handleRefreshNextSync = () => {
        getNextTISync();
    };

    const handleUpdateImports = (e, validID, valid) => {
        zeekForm.set({ ...zeekForm.get(), [validID]: valid });

        setTIImports({
            ...tiImports,
            [e.target.name]: parseInt(e.target.value),
        });
    };

    const handleUpdateUrls = (e, i, validID, valid) => {
        zeekForm.set({ ...zeekForm.get(), [validID]: valid });

        const t = { ...tiImports };
        t[e.target.name][i].url = e.target.value;
        setTIImports(t);
    };

    const handleUpdateTiSource = (e, i, validID, valid) => {
        zeekForm.set({ ...zeekForm.get(), [validID]: valid });

        const t = { ...tiImports };
        t[e.target.name][i].ti_source = e.target.value;
        setTIImports(t);
    };

    const handleAddURL = (e) => {
        const t = { ...tiImports };
        t[e.currentTarget.name].push({ url: '' });
        setTIImports(t);
    };

    const handleRemoveURL = (e, i) => {
        const t = { ...tiImports };
        t[e.currentTarget.name].splice(i, 1);
        if (!t[e.currentTarget.name].length) {
            t[e.currentTarget.name].push({ url: '' });
        }
        setTIImports(t);
    };

    const handleTestURL = (i) => {
        SystemService.checkURL(tiImports.ti_urls[i]).then(() => {
            toast.success('URL connection test successful');
        }, err => {
            toast.error('URL connection failed: ' + err.error);
            console.error('URL connection failed ::: ', err);
        });
    };

    const handleSaveImports = (e, kick) => {
        const ti_urls = tiImports.ti_urls.filter(v => v.url.length > 0);
        TIService.updateConf({
            ...tiImports,
            ti_urls: ti_urls,
        }).then(res => {
            toast.success('Zeek settings saved successfully');
            if (kick) {
                kickIt();
            }
            getURLConf();
        }, err => {
            toast.error('Zeek settings update failed : ', err);
            if (kick) {
                toast.error('Zeek script manual import did not run due to update failure');
            }
        });
    };

    const handleResetImports = () => {
        ZeekService.clearImportsDirectory().then(() => {
            toast.success('Import directory cleared successfully');
        });
        handleOpenDelete();
    };

    const handleOpenDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const kickIt = () => {
        TIService.importAllIndicators().then(res => {
            toast.success('Zeek script import started successfully. Please check audit logs for status.');
        }, err => {
            console.error(err);
        }).finally(getNextTISync);
    };

    const stopIt = () => {
        TIService.manuallyKillImport().then(res => {
            toast.success('Zeek script import canceled');
        }, err => {
            console.error(err);
        }).finally(getNextTISync);
    };

    const isZero = d => {
        return !Moment(d).isValid() || Moment(d).unix() === 0 || d === '0001-01-01T00:00:00Z';
    };

    return (
        <main>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        {/* Don't underestimate the Force. */}
                        {/* <Box ml={2}>
                            {managedStatus ? <Tooltip title="Cannot force import when CMC is managed by MC2">
                                <span>
                                    <Button disabled variant="contained" color="primary" aria-label="manually start import">Force Import</Button>
                                </span>
                            </Tooltip> :
                                <Button variant="contained" color="primary" aria-label="manually start import" onClick={handleKickImports}>Force Import</Button>
                            }
                        </Box> */}

                        {/*stop import button for debugging*/}
                        {/*<Box ml={2}>*/}
                        {/*    <Button*/}
                        {/*        variant="contained"*/}
                        {/*        color="secondary"*/}
                        {/*        aria-label="run now"*/}
                        {/*        onClick={stopIt}*/}
                        {/*        disabled={!tiImports.ti_urls.length || !(checkValidity(zeekForm.get()))}>*/}
                        {/*        Stop import*/}
                        {/*    </Button>*/}
                        {/*</Box>*/}

                        {/*import now*/}
                        {tiSettingsPerms.admin && <Box ml={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                aria-label="run now"
                                onClick={kickIt}
                                disabled={!tiImports.ti_urls.length || !(checkValidity(zeekForm.get())) || changes}>
                                Import Now
                            </Button>
                        </Box>}

                        {tiSettingsPerms.write && <Box ml={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="save"
                                onClick={handleSaveImports}
                                // disabled={!(checkValidity(zeekForm.get()) && !changes)}
                                disabled={!(checkValidity(zeekForm.get()))}
                            >
                                Save
                            </Button>
                        </Box>}
                        <Box ml={2}><Button variant="contained" aria-label="back" onClick={handleGoBack}>Back</Button></Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box p={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">Threat Intelligence Imports</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!tiSettingsPerms.write}
                                            valueType="range"
                                            errorText="Frequency must be within 15 and 1440 minutes"
                                            inputProps={{
                                                min: 15,
                                                max: 1440,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Frequency (min)"
                                            helperText="15m minimum"
                                            name="frequency"
                                            value={tiImports.frequency}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!tiSettingsPerms.write}
                                            valueType="range"
                                            errorText="Timeout must be within 11 and 120"
                                            inputProps={{
                                                min: 11,
                                                max: 120,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Timeout (s)"
                                            helperText="11s minimum"
                                            name="timeout"
                                            value={tiImports.timeout}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!tiSettingsPerms.write}
                                            valueType="range"
                                            errorText="Retries must be within 1 and 20"
                                            inputProps={{
                                                min: 1,
                                                max: 20,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Retries"
                                            name="retries"
                                            value={tiImports.retries}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item container spacing={2}>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box p={3} mb={6}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography>Threat Intel Indicator URLs</Typography>
                                    {tiSettingsPerms.write && <IconButton
                                        size="small"
                                        aria-label="add threat urls"
                                        name="ti_urls"
                                        onClick={handleAddURL}><AddIcon /></IconButton>}
                                </Box>

                                <Box mb={1}>
                                    {/*last imoprt ran on */}
                                    {tiNext && !isZero(tiNext.last) &&
                                        <Typography variant="body2">
                                            {/*display last successful run*/}
                                            Last successful sync occurred on &nbsp;
                                            <b className={classes.next} style={{ paddingRight: 10 }}>
                                                {Moment(tiNext.last).format(theme.date.long)}
                                            </b>
                                        </Typography>
                                    }

                                    {/*next import will run */}
                                    {tiNext && haveImports &&
                                        <Typography variant="body2">
                                            {!tiNext.running &&
                                                    <React.Fragment>
                                                        Next sync scheduled for &nbsp;
                                                        <b className={classes.next} style={{ paddingRight: 10 }}>
                                                            {isZero(tiNext.next) ? 'unknown' : Moment(tiNext.next).format(theme.date.long)}
                                                        </b>
                                                    </React.Fragment>
                                            }

                                            {/*running now*/}
                                            {tiNext.running &&
                                                <b className={classes.next}>Running now </b>
                                            }
                                            <Tooltip title="Refresh">
                                                <CachedIcon
                                                    style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                                                    fontSize="small"
                                                    onClick={handleRefreshNextSync}/>
                                            </Tooltip>
                                        </Typography>
                                    }
                                </Box>

                                <Grid container
                                    direction="row"
                                    justify="flex-start"
                                    alignItems="flex-start"
                                    spacing={2}
                                    style={{ marginTop: 0 }}>
                                    {tiImports.ti_urls.map((v, i) => {
                                        return (
                                            <Grid key={'ti-import-url-' + i}
                                                container
                                                item
                                                xs={12}
                                                spacing={2}
                                                wrap="nowrap"
                                                justifyContent="flex-start"
                                            >
                                                <Grid item xs={8}>
                                                    <Box display="inline-flex" width="100%">
                                                        <Validator
                                                            disabled={!tiSettingsPerms.write}
                                                            valueType="url"
                                                            errorText="URL is invalid"
                                                            allowEmpty
                                                            type="text"
                                                            fullWidth
                                                            placeholder="http://example.com/ti_feed.intel"
                                                            name="ti_urls"
                                                            value={tiImports.ti_urls[i].url}
                                                            onChange={(e, validID, valid) => { handleUpdateUrls(e, i, validID, valid) }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs>
                                                    <Validator
                                                        disabled={!tiSettingsPerms.write}
                                                        errorText="TI Source is required"
                                                        type="text"
                                                        required
                                                        name="ti_urls"
                                                        placeholder="TI Source"
                                                        value={tiImports.ti_urls[i].ti_source || ''}
                                                        onChange={(e, validID, valid) => { handleUpdateTiSource(e, i, validID, valid) }}
                                                    />
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <Tooltip title="Test URL"><IconButton
                                                        name="ti_url_test"
                                                        size="large"
                                                        onClick={() => { handleTestURL(i) }}>
                                                        <TestIcon />
                                                    </IconButton></Tooltip>
                                                    {tiSettingsPerms.delete && <Tooltip title="Delete URL"><IconButton
                                                        color="secondary"
                                                        aria-label="delete url"
                                                        name="ti_urls"
                                                        onClick={(e) => { handleRemoveURL(e, i) }}
                                                        size="large">
                                                        <DeleteIcon />
                                                    </IconButton></Tooltip>}
                                                </Grid>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleOpenDelete}
                onOK={handleResetImports}
                open={deleteOpen}
                message={'Are you sure you want to clear the Zeek script import directory?'}
                title="Delete Imports"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </main>
    );
};

export default TISettings;
